<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LegalDocumentKey } from '@/types/legal'
import { renderMarkdown } from '@/utils/renderMarkdown'

const { t } = useI18n()

const props = defineProps<{
    open: boolean
    initialDocument: LegalDocumentKey
    raceRules: string | null
    termsAndConditions: string | null
    startlineTerms: string | null
    privacyPolicy: string | null
    returnFocusTo: HTMLElement | null
}>()

const emit = defineEmits<{
    'update:open': [value: boolean]
}>()

const active = ref<LegalDocumentKey>(props.initialDocument)
const containerRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const bodyRef = ref<HTMLElement | null>(null)

// Unique title ID for aria-labelledby
let idCounter = 0
const titleId = `legal-drawer-title-${++idCounter}`

const hasRules = computed(() => props.raceRules !== null && props.raceRules.trim() !== '')
const hasTerms = computed(() => props.termsAndConditions !== null && props.termsAndConditions.trim() !== '')
const hasStartlineTerms = computed(() => props.startlineTerms !== null && props.startlineTerms.trim() !== '')
const hasPrivacy = computed(() => props.privacyPolicy !== null && props.privacyPolicy.trim() !== '')
const showSwitcher = computed(
    () => [hasPrivacy.value, hasStartlineTerms.value, hasRules.value, hasTerms.value].filter(Boolean).length > 1,
)

const bodyHtml = computed<string>(() => {
    if (active.value === 'privacy_policy') return props.privacyPolicy ?? ''
    if (active.value === 'startline_terms') return props.startlineTerms ?? ''
    const md = active.value === 'race_rules' ? props.raceRules : props.termsAndConditions
    return md ? renderMarkdown(md) : ''
})

const activeTitle = computed<string>(() => {
    if (active.value === 'privacy_policy') return t('legal_drawer.privacy_policy_title')
    if (active.value === 'startline_terms') return t('legal_drawer.startline_terms_title')
    if (active.value === 'race_rules') return t('legal_drawer.race_rules_title')
    return t('legal_drawer.terms_title')
})

// When switcher tab changes, reset body scroll to top
watch(active, () => {
    nextTick(() => {
        if (bodyRef.value) {
            bodyRef.value.scrollTop = 0
        }
    })
})

function applyOpenState(isOpen: boolean): void {
    if (isOpen) {
        // Set active document from initialDocument prop
        if (props.initialDocument === 'privacy_policy' && hasPrivacy.value) {
            active.value = 'privacy_policy'
        } else if (props.initialDocument === 'startline_terms' && hasStartlineTerms.value) {
            active.value = 'startline_terms'
        } else if (props.initialDocument === 'race_rules' && hasRules.value) {
            active.value = 'race_rules'
        } else if (props.initialDocument === 'terms_and_conditions' && hasTerms.value) {
            active.value = 'terms_and_conditions'
        } else if (hasPrivacy.value) {
            active.value = 'privacy_policy'
        } else if (hasStartlineTerms.value) {
            active.value = 'startline_terms'
        } else if (hasRules.value) {
            active.value = 'race_rules'
        } else if (hasTerms.value) {
            active.value = 'terms_and_conditions'
        }

        document.body.style.overflow = 'hidden'

        nextTick(() => {
            closeButtonRef.value?.focus()
        })
    } else {
        document.body.style.overflow = ''

        // Return focus to trigger element
        const target = props.returnFocusTo
        if (target && document.body.contains(target)) {
            target.focus()
        } else {
            document.body.focus()
        }
    }
}

// Apply open state on mount (handles the case where open is already true when mounted)
onMounted(() => {
    if (props.open) {
        applyOpenState(true)
    }
})

// Watch for subsequent open/close transitions
watch(() => props.open, applyOpenState)

function requestClose(): void {
    emit('update:open', false)
}

function onPrint(): void {
    window.print()
}

function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
        requestClose()
        return
    }
    if (e.key === 'Tab') {
        trapFocus(e)
    }
}

function trapFocus(e: KeyboardEvent): void {
    if (!containerRef.value) return

    const focusable = containerRef.value.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )

    if (focusable.length === 0) return

    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!

    if (e.shiftKey) {
        if (document.activeElement === first) {
            e.preventDefault()
            last.focus()
        }
    } else {
        if (document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
    }
}

// Ensure scroll lock is released if the component is torn down while open
onUnmounted(() => {
    if (props.open) {
        document.body.style.overflow = ''
    }
})
</script>

<template>
    <Teleport to="body">
        <!-- Backdrop -->
        <Transition name="legal-drawer-backdrop">
            <div
                v-if="open"
                aria-hidden="true"
                class="legal-drawer-backdrop"
                @click="requestClose"
            />
        </Transition>

        <!-- Drawer panel -->
        <Transition name="legal-drawer-panel">
            <div
                v-if="open"
                ref="containerRef"
                class="legal-drawer-panel"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="titleId"
                @keydown="onKeydown"
            >
                <!-- Header -->
                <header class="legal-drawer-header">
                    <h2 :id="titleId" class="legal-drawer-title">
                        {{ activeTitle }}
                    </h2>

                    <!-- Segmented switcher — only when more than one document is present -->
                    <div
                        v-if="showSwitcher"
                        class="legal-drawer-switcher"
                        role="tablist"
                        :aria-label="t('legal_drawer.document_label')"
                    >
                        <button
                            v-if="hasPrivacy"
                            type="button"
                            role="tab"
                            :aria-selected="active === 'privacy_policy'"
                            class="legal-drawer-tab"
                            :class="{ 'is-active': active === 'privacy_policy' }"
                            @click="active = 'privacy_policy'"
                        >
                            {{ t('legal_drawer.tab_privacy') }}
                        </button>
                        <button
                            v-if="hasStartlineTerms"
                            type="button"
                            role="tab"
                            :aria-selected="active === 'startline_terms'"
                            class="legal-drawer-tab"
                            :class="{ 'is-active': active === 'startline_terms' }"
                            @click="active = 'startline_terms'"
                        >
                            {{ t('legal_drawer.tab_startline_terms') }}
                        </button>
                        <button
                            v-if="hasRules"
                            type="button"
                            role="tab"
                            :aria-selected="active === 'race_rules'"
                            class="legal-drawer-tab"
                            :class="{ 'is-active': active === 'race_rules' }"
                            @click="active = 'race_rules'"
                        >
                            {{ t('legal_drawer.tab_race_rules') }}
                        </button>
                        <button
                            v-if="hasTerms"
                            type="button"
                            role="tab"
                            :aria-selected="active === 'terms_and_conditions'"
                            class="legal-drawer-tab"
                            :class="{ 'is-active': active === 'terms_and_conditions' }"
                            @click="active = 'terms_and_conditions'"
                        >
                            {{ t('legal_drawer.tab_terms') }}
                        </button>
                    </div>

                    <button
                        ref="closeButtonRef"
                        type="button"
                        class="legal-drawer-close"
                        :aria-label="t('legal_drawer.close')"
                        @click="requestClose"
                    >
                        <!-- × icon -->
                        <svg
                            aria-hidden="true"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                <!-- Body -->
                <div
                    ref="bodyRef"
                    class="legal-drawer-body legal-drawer-prose"
                    role="document"
                    v-html="bodyHtml"
                />

                <!-- Sticky inner footer -->
                <footer class="legal-drawer-footer">
                    <button
                        type="button"
                        class="legal-drawer-print"
                        @click="onPrint"
                    >
                        {{ t('legal_drawer.print') }}
                    </button>
                </footer>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
/* ── Backdrop ── */
.legal-drawer-backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
    background: rgba(0, 0, 0, 0.45);
}

.legal-drawer-backdrop-enter-from,
.legal-drawer-backdrop-leave-to {
    opacity: 0;
}

.legal-drawer-backdrop-enter-active,
.legal-drawer-backdrop-leave-active {
    transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* ── Panel ── */
.legal-drawer-panel {
    /* Design tokens — defined here so the drawer is self-contained in the SPA */
    --color-border:        #D9DCE0;
    --color-border-subtle: #EDEFF2;
    --color-text-primary:   #0F1419;
    --color-text-secondary: #4A5159;
    --color-cta-bg:   #10b981;
    --color-cta-text: #ffffff;
    --color-focus-ring: rgba(16, 185, 129, 0.5);
    --font-heading: system-ui, -apple-system, 'Segoe UI', sans-serif;
    --duration-base: 200ms;
    --easing-standard: cubic-bezier(0.4, 0, 0.2, 1);

    position: fixed;
    z-index: 70;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    /* Mobile: full-width bottom sheet */
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100%;
    max-height: 90vh;
    border-radius: 24px 24px 0 0;
    border-top: 1px solid var(--color-border);
}

/* Desktop: right-side drawer */
@media (min-width: 768px) {
    .legal-drawer-panel {
        inset: 0 0 0 auto;
        bottom: 0;
        top: 0;
        width: clamp(20rem, 40rem, 90vw);
        max-height: 100vh;
        border-radius: 0;
        border-top: none;
        border-left: 1px solid var(--color-border);
    }
}

/* Mobile: slide up from bottom */
.legal-drawer-panel-enter-from,
.legal-drawer-panel-leave-to {
    transform: translateY(100%);
}

.legal-drawer-panel-enter-active {
    transition: transform 400ms cubic-bezier(0, 0, 0.2, 1);
}

.legal-drawer-panel-leave-active {
    transition: transform 400ms cubic-bezier(0.4, 0, 1, 1);
}

/* Desktop: slide in from right */
@media (min-width: 768px) {
    .legal-drawer-panel-enter-from,
    .legal-drawer-panel-leave-to {
        transform: translateX(100%);
    }

    .legal-drawer-panel-enter-active {
        transition: transform 400ms cubic-bezier(0, 0, 0.2, 1);
    }

    .legal-drawer-panel-leave-active {
        transition: transform 400ms cubic-bezier(0.4, 0, 1, 1);
    }
}

/* ── Header ── */
.legal-drawer-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
}

.legal-drawer-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-text-primary);
    font-family: var(--font-heading);
    margin: 0;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Segmented switcher ──
   Mobile: a 2-column grid so up to four document tabs wrap onto two rows
   instead of forcing a single row that cannot shrink (the header's other
   siblings - the truncating title and the close button - would otherwise
   be squeezed off / the panel would overflow horizontally). Desktop keeps
   the original single-row segmented control (see the min-width: 768px
   override below). */
.legal-drawer-switcher {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    overflow: hidden;
    flex-shrink: 0;
}

.legal-drawer-tab {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-right: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    text-align: center;
    cursor: pointer;
    transition: background-color var(--duration-base) var(--easing-standard),
                color var(--duration-base) var(--easing-standard);
}

/* Right-hand column of the mobile grid does not need an inner right border. */
.legal-drawer-tab:nth-child(even) {
    border-right: none;
}

/* Last tab never needs a bottom border. */
.legal-drawer-tab:last-child {
    border-bottom: none;
}

/* When the tab count is even, the second-to-last tab is the left cell of the
   final (complete) row and does not need a bottom border either. */
.legal-drawer-tab:nth-last-child(2):nth-child(odd) {
    border-bottom: none;
}

/* When the tab count is odd, the last tab sits alone in the final row -
   span both columns instead of leaving a half-empty row with a dangling
   right border. Scoped to the mobile grid layout only: at the desktop
   breakpoint the switcher becomes a flex row (grid-column is inert there
   anyway), and this selector's specificity would otherwise beat the
   desktop ":last-child" reset below and mask any future desktop grid use. */
@media (max-width: 767px) {
    .legal-drawer-tab:last-child:nth-child(odd) {
        grid-column: 1 / -1;
        border-right: none;
    }
}

.legal-drawer-tab.is-active {
    background: var(--color-cta-bg);
    color: var(--color-cta-text);
}

.legal-drawer-tab:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: -2px;
}

/* Desktop: restore the original single-row segmented control. */
@media (min-width: 768px) {
    .legal-drawer-switcher {
        display: flex;
        grid-template-columns: none;
    }

    .legal-drawer-tab {
        border-bottom: none;
    }

    .legal-drawer-tab:not(:last-child) {
        border-right: 1px solid var(--color-border);
    }

    .legal-drawer-tab:last-child {
        border-right: none;
        grid-column: auto;
    }
}

/* ── Close button ── */
.legal-drawer-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.375rem;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color var(--duration-base) var(--easing-standard),
                color var(--duration-base) var(--easing-standard);
}

.legal-drawer-close:hover {
    background-color: color-mix(in srgb, var(--color-text-secondary) 12%, transparent);
    color: var(--color-text-primary);
}

.legal-drawer-close:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
}

/* ── Body ── */
.legal-drawer-body {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1.5rem 1.25rem;
}

/* ── Prose styles for rendered Markdown ── */
.legal-drawer-prose :deep(h1),
.legal-drawer-prose :deep(h2),
.legal-drawer-prose :deep(h3),
.legal-drawer-prose :deep(h4) {
    color: var(--color-text-primary);
    font-family: var(--font-heading);
    font-weight: 700;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.legal-drawer-prose :deep(h1) { font-size: 1.375rem; }
.legal-drawer-prose :deep(h2) { font-size: 1.125rem; }
.legal-drawer-prose :deep(h3) { font-size: 1rem; }
.legal-drawer-prose :deep(h4) { font-size: 0.9375rem; }

.legal-drawer-prose :deep(h1:first-child),
.legal-drawer-prose :deep(h2:first-child),
.legal-drawer-prose :deep(h3:first-child) {
    margin-top: 0;
}

.legal-drawer-prose :deep(p) {
    font-size: 0.9375rem;
    line-height: 1.7;
    color: var(--color-text-primary);
    margin-bottom: 0.875rem;
}

.legal-drawer-prose :deep(p:last-child) {
    margin-bottom: 0;
}

.legal-drawer-prose :deep(ul),
.legal-drawer-prose :deep(ol) {
    padding-left: 1.5rem;
    margin-bottom: 0.875rem;
    font-size: 0.9375rem;
    line-height: 1.7;
    color: var(--color-text-primary);
}

.legal-drawer-prose :deep(ul) { list-style-type: disc; }
.legal-drawer-prose :deep(ol) { list-style-type: decimal; }

.legal-drawer-prose :deep(li) {
    margin-bottom: 0.375rem;
}

.legal-drawer-prose :deep(a) {
    color: var(--color-cta-bg);
    text-decoration: underline;
}

.legal-drawer-prose :deep(strong) {
    font-weight: 700;
    color: var(--color-text-primary);
}

.legal-drawer-prose :deep(em) {
    font-style: italic;
}

.legal-drawer-prose :deep(hr) {
    border: none;
    border-top: 1px solid var(--color-border-subtle);
    margin: 1.5rem 0;
}

.legal-drawer-prose :deep(blockquote) {
    border-left: 3px solid var(--color-border);
    padding-left: 1rem;
    margin-left: 0;
    color: var(--color-text-secondary);
    font-style: italic;
}

.legal-drawer-prose :deep(table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    margin-bottom: 0.875rem;
}

.legal-drawer-prose :deep(th) {
    text-align: left;
    font-weight: 600;
    color: #0f172a;
    padding: 0.5rem 0.75rem 0.5rem 0;
    border-bottom: 1px solid #e2e8f0;
}

.legal-drawer-prose :deep(td) {
    padding: 0.5rem 0.75rem 0.5rem 0;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    color: #334155;
}

.legal-drawer-prose :deep(code) {
    background-color: color-mix(in srgb, var(--color-text-secondary) 12%, transparent);
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.8125rem;
}

/* ── Sticky inner footer ── */
.legal-drawer-footer {
    border-top: 1px solid var(--color-border-subtle);
    padding: 0.875rem 1.25rem;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
}

.legal-drawer-print {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
    border: 1px solid var(--color-border);
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: background-color var(--duration-base) var(--easing-standard),
                color var(--duration-base) var(--easing-standard),
                border-color var(--duration-base) var(--easing-standard);
}

.legal-drawer-print:hover {
    background-color: color-mix(in srgb, var(--color-text-secondary) 8%, transparent);
    color: var(--color-text-primary);
    border-color: var(--color-text-secondary);
}

.legal-drawer-print:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
}
</style>
